import { useEffect, useState } from "react";
import { Menu, X, ChevronDown, Phone, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { BRAND, COURSE_CATEGORIES, COUNTRIES } from "@/data/content";
import { useEnquiryPopup } from "@/context/EnquiryPopupContext";
import logoIcon from "@/assets/logo-icon.png";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Courses", href: "#courses", mega: "courses" },
  { label: "Study Abroad", href: "#abroad", mega: "abroad" },
  { label: "Placements", href: "#partners" },
  { label: "Blog", href: "#blog" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [openMega, setOpenMega] = useState(null);
  const { openPopup } = useEnquiryPopup();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobile ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobile]);

  const scrollTo = (href) => {
    setMobile(false);
    setOpenMega(null);
    setTimeout(() => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background,box-shadow,border] duration-300 ${
          scrolled
            ? "bg-white/85 backdrop-blur-xl border-b border-black/5 shadow-[0_8px_30px_rgba(10,61,145,0.06)]"
            : "bg-white/60 backdrop-blur-md border-b border-transparent"
        }`}
      >
        <div className="container-x flex items-center justify-between h-16 md:h-20 gap-4">
          {/* Logo */}
          <button
            data-testid="brand-logo"
            onClick={() => scrollTo("#home")}
            className="flex items-center gap-2.5 shrink-0"
          >
            <span className="relative inline-flex h-11 w-11 shrink-0 items-center justify-center">
              <img
                src={logoIcon}
                alt="IIUTCStudy logo"
                className="h-full w-full object-contain drop-shadow-[0_6px_16px_rgba(10,61,145,0.28)]"
              />
            </span>
            <span className="flex flex-col leading-tight text-left">
              <span className="font-display font-extrabold text-base sm:text-lg tracking-tight whitespace-nowrap">
                <span className="text-[#B31942]">IIUTC</span>
                <span className="text-[#0A3161]">Study</span>
              </span>
              <span className="hidden sm:block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium whitespace-nowrap">
                Learn · Upskill · Grow · Succeed
              </span>
            </span>
          </button>

          {/* Center nav */}
          <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {NAV.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.mega && setOpenMega(item.mega)}
                onMouseLeave={() => item.mega && setOpenMega(null)}
              >
                <button
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => scrollTo(item.href)}
                  className="px-3 py-2 rounded-full text-[14px] xl:text-[15px] font-medium text-slate-700 hover:text-[#0A3161] inline-flex items-center gap-1 transition-colors whitespace-nowrap"
                >
                  {item.label}
                  {item.mega && <ChevronDown className="h-3.5 w-3.5 opacity-60" strokeWidth={2.25} />}
                </button>
                <AnimatePresence>
                  {item.mega && openMega === item.mega && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.18 }}
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[min(720px,88vw)]"
                    >
                      <div className="rounded-3xl bg-white border border-black/5 premium-shadow p-6">
                        {item.mega === "courses" ? (
                          <div className="grid grid-cols-3 gap-3">
                            {COURSE_CATEGORIES.map((c) => (
                              <button
                                key={c.key}
                                onClick={() => scrollTo("#courses")}
                                className="text-left p-3 rounded-2xl hover:bg-[#F8F9FA] transition-colors"
                                data-testid={`mega-course-${c.key}`}
                              >
                                <div className="text-sm font-semibold text-[#0A3161]">{c.title}</div>
                                <div className="text-xs text-slate-500 mt-1 line-clamp-2">{c.blurb}</div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="grid grid-cols-3 gap-2">
                            {COUNTRIES.slice(0, 12).map((c) => (
                              <button
                                key={c.name}
                                onClick={() => scrollTo("#abroad")}
                                className="flex items-center gap-2 p-2.5 rounded-2xl hover:bg-[#F8F9FA] text-left transition-colors"
                                data-testid={`mega-country-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                              >
                                <span className="text-xl">{c.flag}</span>
                                <span className="text-sm font-medium text-slate-800">{c.name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>

          {/* Right CTA */}
          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <a
              data-testid="header-call"
              href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
              className="hidden lg:inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-slate-700 hover:text-[#0A3161] transition-colors whitespace-nowrap"
            >
              <Phone className="h-4 w-4" strokeWidth={2.25} /> {BRAND.phone}
            </a>
            <motion.button
              data-testid="header-apply"
              onClick={() => openPopup()}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-[#B31942] text-white hover:bg-[#940835] shadow-[0_10px_30px_rgba(229,57,53,0.32)] transition-colors whitespace-nowrap"
            >
              Apply Now
              <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            </motion.button>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobile((s) => !s)}
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-2xl border border-black/10 bg-white text-slate-800 hover:border-[#0A3161]/30 transition-colors"
            aria-label="Toggle menu"
          >
            {mobile ? <X className="h-5 w-5" strokeWidth={2.25} /> : <Menu className="h-5 w-5" strokeWidth={2.25} />}
          </button>
        </div>
      </header>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {mobile && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobile(false)}
              className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm lg:hidden"
              data-testid="mobile-overlay"
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="fixed top-0 right-0 z-50 h-full w-[85%] max-w-sm bg-white shadow-2xl lg:hidden flex flex-col"
              data-testid="mobile-drawer"
            >
              <div className="h-16 px-5 flex items-center justify-between border-b border-black/5">
                <div className="flex items-center gap-2.5">
                  <span className="h-10 w-10 shrink-0 flex items-center justify-center">
                    <img src={logoIcon} alt="IIUTCStudy logo" className="h-full w-full object-contain" />
                  </span>
                  <div className="leading-tight">
                    <div className="font-display font-extrabold text-base">
                      <span className="text-[#B31942]">IIUTC</span>
                      <span className="text-[#0A3161]">Study</span>
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.14em] text-slate-500">Learn · Upskill · Grow</div>
                  </div>
                </div>
                <button
                  onClick={() => setMobile(false)}
                  className="h-10 w-10 rounded-2xl border border-black/10 flex items-center justify-center hover:border-[#0A3161]/30 transition-colors"
                  aria-label="Close menu"
                  data-testid="mobile-close"
                >
                  <X className="h-5 w-5" strokeWidth={2.25} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-6">
                <nav className="flex flex-col gap-1">
                  {NAV.map((n, i) => (
                    <motion.button
                      key={n.label}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.04 }}
                      data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => scrollTo(n.href)}
                      className="w-full text-left px-4 py-3.5 rounded-2xl text-slate-800 hover:bg-[#F8F9FA] hover:text-[#0A3161] font-medium flex items-center justify-between group transition-colors"
                    >
                      {n.label}
                      <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-[#B31942] group-hover:translate-x-0.5 transition-transform" strokeWidth={2.25} />
                    </motion.button>
                  ))}
                </nav>

                <div className="mt-8 pt-6 border-t border-black/5 space-y-3">
                  <a
                    href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
                    className="flex items-center gap-3 p-3.5 rounded-2xl bg-[#F8F9FA] text-slate-800"
                    data-testid="mobile-call"
                  >
                    <span className="h-10 w-10 rounded-xl bg-[#0A3161]/10 text-[#0A3161] flex items-center justify-center">
                      <Phone className="h-4 w-4" strokeWidth={2.25} />
                    </span>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400">Call</div>
                      <div className="text-sm font-semibold">{BRAND.phone}</div>
                    </div>
                  </a>
                </div>
              </div>

              <div className="p-5 border-t border-black/5">
                <motion.button
                  data-testid="mobile-apply"
                  whileTap={{ scale: 0.97 }}
                  onClick={() => {
                    setMobile(false);
                    openPopup();
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-sm font-semibold bg-[#B31942] text-white shadow-[0_10px_30px_rgba(229,57,53,0.28)]"
                >
                  Apply Now <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                </motion.button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
